"use client"

import { heIL } from "@mui/material/locale"
import { createTheme, ThemeOptions } from "@mui/material/styles"
import { createBreakpoints } from "@mui/system"
import { Font } from "components/shopFeatures/wizard/constants"
import type { AppColors } from "./types"

const colors = {
  black: "#000000",
  white: "#ffffff",
  chineseBlack: "#161616",
  charlestonGrey: "#2b2b2b",
  borderGrey: "#d5d5d5",
  lightGrey: "#f5f5f5",
  mazdaRed: "#920a2d",
  arsenic: "#414141",
  philippineGray: "#808080",
  cssYellow: "yellow",
  darkGrey: "#0000001f",
}

const breakpoints = createBreakpoints({})

const muiOptions: ThemeOptions = {
  direction: "rtl",

  // keep old v4 breakpoints so layout doesn't shift
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      smallLaptop: 1080,
      mediumLaptop: 1550,
      lg: 1280,
      xl: 1920,
    },
  },

  palette: {
    primary: {
      main: colors.charlestonGrey,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.white,
      contrastText: colors.chineseBlack,
    },
    text: {
      primary: colors.chineseBlack,
      secondary: colors.chineseBlack,
    },
    error: {
      main: "#b50303",
    },
  },
  shadows: new Array(25).fill("none") as any,
  shape: { borderRadius: 2 },
  typography: {
    fontFamily: "MazdaType, OENaPro, sans-serif;",
    h2: {
      fontSize: "1.125rem",
      fontWeight: 300,
      letterSpacing: "0.72px",
      margin: "16px 0",
      [breakpoints.up("md")]: { fontSize: "1.25rem" },
    },
    h3: {
      fontSize: "0.875rem",
      fontWeight: 600,
      letterSpacing: "0.56px",
      marginTop: "24px",
      marginBottom: "20px",
      borderBottom: `1px solid ${colors.chineseBlack}`,
      paddingBottom: "8px",
      [breakpoints.up("md")]: { fontSize: "1rem" },
    },
    h4: {
      fontSize: "0.875rem",
      fontWeight: 600,
      letterSpacing: "0.84px",
      marginTop: "20px",
      marginBottom: "8px",
      color: colors.chineseBlack,
      [breakpoints.up("md")]: { fontSize: "1rem" },
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.43",
      letterSpacing: "0.1em",
      color: colors.black,
    },
  },

  components: {
    MuiFormControl: {
      defaultProps: { fullWidth: true },
      styleOverrides: {
        root: {
          marginBottom: 4,
          legend: {
            width: 0,
          },
        }, // replaces spacing(0.5)
      },
    },
    MuiTextField: {
      defaultProps: { fullWidth: true },
      styleOverrides: {
        root: {},
      },
    },
    MuiInput: {
      defaultProps: { disableUnderline: true },
      styleOverrides: {
        input: {
          border: `1px solid ${colors.chineseBlack}`,
          borderRadius: 2,
          height: 40,
          boxSizing: "border-box",
          padding: "8px",
        },
        root: {
          "&.Mui-disabled": {
            color: colors.chineseBlack,
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "always",
      },
      styleOverrides: {
        underlineAlways: {
          textDecoration: "underline !important",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { marginTop: "0 !important" },
        input: {
          height: "45px !important",
          padding: "0 10px !important",
          direction: "inherit",
          "&.MuiNativeSelect-select": {
            padding: "0 10px 0 20px !important",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px",
            borderColor: colors.chineseBlack,
          },
        },
        notchedOutline: {
          borderWidth: "1px",
          borderColor: colors.chineseBlack,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          direction: "rtl",
          color: colors.chineseBlack,
        },
        outlined: {
          transform: "translate(-10px, 24px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(0px, 0px) scale(1)",
            color: colors.chineseBlack,
          },
          "&.Mui-focused": {
            color: colors.chineseBlack,
          },
        },
        formControl: {
          position: "static",
        },
        shrink: { transform: "none" },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          flexBasis: "100%",
          direction: "rtl",
          color: colors.chineseBlack,
          fontFamily: `${Font.OENaPro}`,
          fontSize: 16,
          fontWeight: 300,
          lineHeight: "22px",
          right: 0,
          "&.Mui-focused": {
            color: colors.chineseBlack,
            "& .MuiFormLabel-asterisk": {
              color: colors.mazdaRed,
            },
          },
          "&.Mui-error": {
            color: colors.chineseBlack,
          },
          "& + .MuiInput-formControl": {
            marginTop: 6,
          },
        },
        asterisk: {
          color: colors.mazdaRed,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
          fontSize: 12,
          textAlign: "right",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          marginRight: "-11px",
          marginLeft: "16px",
          verticalAlign: "middle",
          WebkitTapHighlightColor: "transparent",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        },
      },
      styleOverrides: {
        select: {
          borderRadius: 3,
          height: "45px !important",
          border: "1px solid #999",
          display: "flex",
          alignItems: "center",
          "&:focus": {
            borderRadius: 3,
            border: "1px solid #999",
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          "& .MuiBackdrop-root": {
            backgroundColor: "transparent",
            backdropFilter: "blur(0px)",
          },
        },
      },
    },
    MuiNativeSelect: {
      styleOverrides: {
        icon: {
          top: "calc(50% - 12px)",
          color: "rgba(0, 0, 0, 0.54)",
          left: 0,
          right: "initial",
          position: "absolute",
          pointerEvents: "none",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: {
          "&.Mui-checked": {
            color: colors.black,
          },
        },
        root: {
          "&.orderCheckbox + div span, &.orderCheckbox + span": {
            fontFamily: Font.OENaPro,
            fontSize: "12px",
            fontWeight: 300,
            lineHeight: "16px",
            letterSpacing: "0em",
            a: {
              textDecoration: "underline !important",
            },
            "&.MuiFormControlLabel-asterisk": {
              visibility: "hidden",
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&:focus": { outline: "1px solid currentColor" },
        },
        contained: {
          "&.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.26)",
            boxShadow: "none",
            backgroundColor: colors.darkGrey,
          },
        },
        containedPrimary: {
          backgroundColor: colors.mazdaRed,
          color: colors.white,
          fontSize: "1rem",
          fontWeight: 600,
          letterSpacing: "0.64px",
          "&:hover": {
            backgroundColor: colors.mazdaRed,
          },
          "@media (hover: none)": {
            "&:hover": { backgroundColor: colors.mazdaRed },
          },
          "&:focus": {
            backgroundColor: colors.white,
            color: colors.mazdaRed,
            outline: `2px solid ${colors.mazdaRed}`,
          },
        },
        startIcon: {
          marginLeft: "8px",
          marginRight: "-4px",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        colorSecondary: {
          backgroundColor: colors.philippineGray,
          "&:hover": { backgroundColor: colors.philippineGray },
        },
        root: {
          "&:hover": { backgroundColor: colors.philippineGray },
        },
      },
    },
    MuiTouchRipple: {
      styleOverrides: {
        rippleVisible: {
          opacity: 0.15,
          animationDuration: "550ms",
          animationTimingFunction: "ease-in-out",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: "12px",
          "&.authDialog": {
            width: "328px !important",
          },
          "&.leadForm": {
            width: "670px !important",
            maxWidth: "670px",
          },
          "&.summaryTestDrive": {
            width: "670px !important",
            maxWidth: "670px",
            padding: "0 30px",
          },
        },
        paperWidthMd: {
          width: "560px",
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(5.5px)",
          justifyContent: "center",
          backgroundColor: "rgba(16, 16, 16, 0.4)",
          webkitBackdropFilter: "blur(5.5px)",
          WebkitTapHighlightColor: "transparent",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          width: "670px !important",
        },
      },
    },
  },
}

// Create MUI theme with locale and then extend with custom fields
const baseTheme = createTheme(muiOptions, heIL)

const shopTheme = {
  ...baseTheme,
  colors,
}

export default shopTheme
export type ShopTheme = typeof shopTheme

// augment MUI theme types for custom color field
declare module "@mui/material/styles" {
  interface Theme {
    colors: AppColors
  }
  interface ThemeOptions {
    colors?: AppColors
  }
}
